import { z } from "zod";
import { providerKeySchema } from "./llm-providers.schemas";

export type ApiProviderKey = z.infer<typeof providerKeySchema>;

export const DEFAULT_ENABLED_API_PROVIDERS: readonly ApiProviderKey[] = [
  "openai",
];

export const OPENAI_PICKER_MODEL_IDS = new Set([
  "gpt-6.1-sol",
  "gpt-6-astra",
  "gpt-6-luna",
]);

const apiProviderKeys = new Set<ApiProviderKey>(providerKeySchema.options);

export function isApiProviderKey(value: string): value is ApiProviderKey {
  return apiProviderKeys.has(value as ApiProviderKey);
}

export function parseEnabledApiProviders(
  rawValue: string | undefined,
): readonly ApiProviderKey[] {
  const configured = (rawValue ?? "")
    .split(",")
    .map((value) => value.trim().toLowerCase())
    .filter((value): value is ApiProviderKey => isApiProviderKey(value));

  if (configured.length === 0) {
    return DEFAULT_ENABLED_API_PROVIDERS;
  }

  return [...new Set(configured)];
}

export function getEnabledApiProviderKeys(
  rawValue: string | undefined,
): ApiProviderKey[] {
  return [...parseEnabledApiProviders(rawValue)];
}

export function isApiProviderEnabled(
  providerKey: string,
  rawValue: string | undefined,
): boolean {
  return parseEnabledApiProviders(rawValue).includes(
    providerKey as ApiProviderKey,
  );
}

export function assertApiProviderEnabled(
  providerKey: string,
  rawValue: string | undefined,
): void {
  if (isApiProviderEnabled(providerKey, rawValue)) {
    return;
  }

  throw new Error(
    `API provider "${providerKey}" is disabled by ENABLED_API_PROVIDERS. Add "${providerKey}" to the flag to enable it.`,
  );
}

export function filterEnabledApiProviders<T extends { providerKey: string }>(
  providers: readonly T[],
  rawValue: string | undefined,
): T[] {
  return providers.filter((provider) =>
    isApiProviderEnabled(provider.providerKey, rawValue),
  );
}

export function filterOpenAiPickerModels(
  providerKey: string,
  models: readonly string[],
): string[] {
  if (providerKey !== "openai") return [...models];
  return models.filter((model) => OPENAI_PICKER_MODEL_IDS.has(model));
}
